import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Sparkles, Trophy, Flag } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useScreenCleanMode } from "@/lib/mobileFloatingControls";
import {
  useQuickReturn,
  clearQuickReturn,
  type QuickReturnKind,
} from "@/lib/mobile/quickReturn";
import { trackEvent } from "@/lib/audit";

const ICONS: Record<QuickReturnKind, typeof Sparkles> = {
  prediction: Sparkles,
  challenge: Trophy,
  ranking: Flag,
};

/**
 * WORLD-CUP-MOBILE-UX-FINAL-P0 — 1-tap return chip (mobile only).
 * Surfaces the user's last meaningful action when they re-enter the app,
 * so they can resume in a single tap. Degrades to nothing when empty.
 */
export function MobileQuickReturnChip() {
  const isMobile = useIsMobile();
  const cleanScreen = useScreenCleanMode();
  const entry = useQuickReturn();

  useEffect(() => {
    if (!isMobile || cleanScreen || !entry) return;
    void trackEvent("mobile_quick_return_shown", { kind: entry.kind });
  }, [isMobile, cleanScreen, entry]);

  if (!isMobile || cleanScreen || !entry) return null;
  const Icon = ICONS[entry.kind] ?? Sparkles;

  return (
    <div
      className="fixed inset-x-0 z-[55] flex justify-center px-3 lg:hidden"
      style={{ top: "calc(env(safe-area-inset-top) + 64px)" }}
    >
      <Link
        to={entry.href}
        onClick={() => {
          void trackEvent("mobile_quick_return_clicked", { kind: entry.kind });
          clearQuickReturn();
        }}
        className="flex min-h-[44px] max-w-full items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-2 text-[12px] font-black text-foreground shadow-lg backdrop-blur"
      >
        <Icon className="h-4 w-4 shrink-0 text-primary" />
        <span className="truncate">{entry.label}</span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary" />
      </Link>
    </div>
  );
}